/*
 * Kartensymbole (Patrick, 23.10.2026). Das Icon zeigt das Hauptgeraet der
 * Anlage, kleine Zusatzsymbole die weiteren Kategorien, der Ring den Zustand:
 * gruen ok, gelb Warnung oder seit ueber 15 min kein Wert, rot aktiver Alarm
 * mit Zaehler, grau gestrichelt offline. Rot nur fuer Alarme; die Hausfarbe
 * des Mandanten bleibt der Navigation.
 *
 * Anlagensymbole oeffnen per Klick das Popup (MapPage.syncMarkers), weil sie
 * aufgefaechert neben ihrem Punkt liegen koennen. Cluster lassen Klicks durch
 * an die unsichtbare Kreis-Ebene, die zum Hineinzoomen fuehrt.
 */
import type { AlarmLevel, MapAssetDto } from '../api/types';
import { categoryInfo, iconKey, iconSvg } from '../components/deviceCategories';

export type PlantState = 'ok' | 'warn' | 'alarm' | 'offline';

/** Alarm vor offline vor Warnung: ein offener Alarm bleibt sichtbar, auch
 *  wenn die Anlage gerade nicht erreichbar ist. */
export function plantState(a: Pick<MapAssetDto, 'alarm' | 'online' | 'stale'>): PlantState {
  if (a.alarm === 'Alarm' || a.alarm === 'Critical') return 'alarm';
  if (!a.online) return 'offline';
  if (a.alarm === 'Warning' || a.stale) return 'warn';
  return 'ok';
}

export const STATE_LABEL: Record<PlantState, string> = {
  ok: 'in Ordnung',
  warn: 'Warnung oder Werte veraltet',
  alarm: 'Alarm aktiv',
  offline: 'offline',
};

/** Hauptsymbol und bis zu zwei Zusatzsymbole (verschiedene Kategorien),
 *  danach die Zahl der uebrigen. Equipment kommt vom Server schon sortiert. */
export function markerIcons(equipment: MapAssetDto['equipment']): { main: string; subs: string[]; more: number } {
  const list = equipment ?? [];
  if (list.length === 0) return { main: 'Plant', subs: [], more: 0 };
  const main = iconKey(list[0].category, list[0].variant);
  const seen = new Set<string>([list[0].category]);
  const rest: string[] = [];
  for (const e of list.slice(1)) {
    if (seen.has(e.category)) continue;
    seen.add(e.category);
    rest.push(iconKey(e.category, e.variant));
  }
  if (rest.length <= 2) return { main, subs: rest, more: 0 };
  return { main, subs: rest.slice(0, 1), more: rest.length - 1 };
}

export function equipmentText(equipment: MapAssetDto['equipment']): string {
  const list = equipment ?? [];
  if (list.length === 0) return 'keine Kategorie eingetragen';
  return list.map((e) => {
    const info = categoryInfo(e.category);
    const v = info?.variants?.find((x) => x.key === e.variant)?.label;
    return `${info?.label ?? e.category}${v ? ` (${v})` : ''}${e.isPrimary ? ' · Hauptgerät' : ''}`;
  }).join(', ');
}

export interface MarkerProps {
  state: PlantState;
  main: string;
  subs: string;
  more: number;
  alarmCount: number;
  service: boolean;
}

export function markerHtml(p: MarkerProps): string {
  const subs = p.subs ? p.subs.split(',').filter(Boolean) : [];
  const parts: string[] = [iconSvg(p.main, 20)];
  subs.forEach((k, i) => {
    parts.push(`<span class="hv-mk__sub hv-mk__sub--${i === 0 ? 'r' : 'l'}">${iconSvg(k, 11)}</span>`);
  });
  if (p.more > 0) {
    parts.push(`<span class="hv-mk__sub hv-mk__sub--l hv-mk__sub--more">+${p.more}</span>`);
  }
  if (p.state === 'alarm' && p.alarmCount > 0) {
    parts.push(`<span class="hv-mk__badge">${p.alarmCount > 99 ? '99+' : p.alarmCount}</span>`);
  }
  if (p.service) parts.push(`<span class="hv-mk__svc" title="Servicezugang offen">${iconSvg('Service', 10)}</span>`);
  return parts.join('');
}

/** Cluster: neutraler Kreis mit Anzahl, der Ring zeigt die Anteile. */
export function clusterSvg(total: number, alarm: number, warn: number, offline: number): string {
  const r = 19, c = 2 * Math.PI * r;
  const ok = Math.max(0, total - alarm - warn - offline);
  const segs: [number, string][] = [[ok, 'var(--hv-mk-ok)'], [warn, 'var(--hv-mk-warn)'], [alarm, 'var(--hv-mk-alarm)'], [offline, 'var(--hv-mk-offline)']];
  let offset = 0;
  const rings = segs.filter(([n]) => n > 0).map(([n, col]) => {
    const len = (n / total) * c;
    const el = `<circle cx="24" cy="24" r="${r}" fill="none" stroke="${col}" stroke-width="4"`
      + ` stroke-dasharray="${len.toFixed(2)} ${(c - len).toFixed(2)}" stroke-dashoffset="${(-offset).toFixed(2)}"`
      + ` transform="rotate(-90 24 24)"/>`;
    offset += len;
    return el;
  }).join('');
  const label = total > 999 ? `${Math.round(total / 100) / 10}k` : String(total);
  return `<svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">`
    + `<circle cx="24" cy="24" r="${r}" class="hv-mk-cl__bg"/>${rings}`
    + `<text x="24" y="28.5" text-anchor="middle" class="hv-mk-cl__n">${label}</text></svg>`;
}

export function alarmLevelRank(a: AlarmLevel): number {
  return a === 'Critical' ? 3 : a === 'Alarm' ? 2 : a === 'Warning' ? 1 : 0;
}
